import { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Briefcase, Wrench, Camera, Loader2, X } from 'lucide-react';
import { useTranslation } from '../../context/LanguageContext';
import { getMomentByUuid, getMomentPhotoUrl } from '../../services/momentsService';

/**
 * MomentDetailPage - Public view of a moment (as other users see it)
 * Props:
 *   momentUuid {string} - UUID of the moment to display
 *   onClose {function} - Called when user closes the detail view
 */
const MomentDetailPage = ({ momentUuid, onClose }) => {
    const { t, language } = useTranslation();
    const tm = t.moments || {};

    const [moment, setMoment] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [currentIndex, setCurrentIndex] = useState(0);

    useEffect(() => {
        if (!momentUuid) return;
        const load = async () => {
            setIsLoading(true);
            const data = await getMomentByUuid(momentUuid);
            setMoment(data);
            setIsLoading(false);
        };
        load();
    }, [momentUuid]);

    // Keyboard navigation
    useEffect(() => {
        const handleKey = (e) => {
            if (e.key === 'Escape') onClose?.();
            if (e.key === 'ArrowLeft') goToPrevious();
            if (e.key === 'ArrowRight') goToNext();
        };
        window.addEventListener('keydown', handleKey);
        return () => window.removeEventListener('keydown', handleKey);
    }, [currentIndex, moment]);

    // Lock body scroll
    useEffect(() => {
        document.body.style.overflow = 'hidden';
        return () => { document.body.style.overflow = ''; };
    }, []);

    if (isLoading) {
        return (
            <div className="fixed inset-0 z-[9999] bg-black/95 flex items-center justify-center">
                <Loader2 className="w-10 h-10 text-amber-500 animate-spin" />
            </div>
        );
    }

    if (!moment) {
        return (
            <div className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center text-white">
                <div className="text-center">
                    <Camera className="w-16 h-16 mx-auto text-gray-600 mb-4" />
                    <p className="text-gray-400">{tm.notFound || 'Moment not found'}</p>
                    <button onClick={onClose} className="mt-4 px-4 py-2 bg-white/10 rounded-lg hover:bg-white/20">
                        {tm.actions?.close || 'Close'}
                    </button>
                </div>
            </div>
        );
    }

    const photos = moment.photos ?? moment.PHOTOS ?? [];
    const skills = moment.skills ?? moment.SKILLS ?? [];

    const getOccupationName = () => {
        const key = `occupationname${language}`;
        return moment[key] || moment.occupationnamefr || '';
    };

    const getSkillName = (skill) => {
        const key = `name${language}`;
        return skill[key] || skill.namefr || '';
    };

    const goToPrevious = () => {
        if (photos.length < 2) return;
        setCurrentIndex(prev => (prev === 0 ? photos.length - 1 : prev - 1));
    };

    const goToNext = () => {
        if (photos.length < 2) return;
        setCurrentIndex(prev => (prev === photos.length - 1 ? 0 : prev + 1));
    };

    const currentPhoto = photos[currentIndex];
    const imagePath = currentPhoto?.imagepath ?? currentPhoto?.imagePath ?? '';
    const caption = currentPhoto?.caption ?? '';
    const occupationName = getOccupationName();

    return (
        <div className="fixed inset-0 sm:left-20 z-[9999] bg-black/95 flex flex-col">
            {/* Header */}
            <div className="flex items-start justify-between p-4 text-white shrink-0">
                <div className="flex-1 min-w-0">
                    <h2 className="text-xl font-bold truncate">{moment.title}</h2>
                    <div className="flex items-center gap-4 mt-1 text-sm text-gray-400 flex-wrap">
                        {occupationName && (
                            <span className="flex items-center gap-1">
                                <Briefcase className="w-4 h-4 shrink-0" />
                                {occupationName}
                            </span>
                        )}
                        {photos.length > 0 && (
                            <span>{currentIndex + 1} / {photos.length}</span>
                        )}
                    </div>
                </div>
                <button
                    onClick={onClose}
                    className="ml-4 p-2 text-white/80 hover:text-white hover:bg-white/10 rounded-full transition-colors shrink-0"
                >
                    <X className="w-6 h-6" />
                </button>
            </div>

            {/* Main content */}
            <div className="flex-1 flex flex-col lg:flex-row overflow-hidden">
                {/* Image area */}
                <div className="flex-1 flex items-center justify-center relative px-12 py-4">
                    {photos.length === 0 ? (
                        <div className="flex flex-col items-center text-gray-600">
                            <Camera className="w-20 h-20 mb-3" />
                            <p className="text-sm">{tm.noPhotos || 'No photos'}</p>
                        </div>
                    ) : (
                        <>
                            {photos.length > 1 && (
                                <button
                                    onClick={goToPrevious}
                                    className="absolute left-2 p-3 text-white/80 hover:text-white bg-white/10 hover:bg-white/20 rounded-full transition-colors"
                                >
                                    <ChevronLeft className="w-7 h-7" />
                                </button>
                            )}

                            <img
                                key={currentIndex}
                                src={getMomentPhotoUrl(imagePath)}
                                alt={moment.title}
                                className="max-w-full max-h-[calc(100vh-320px)] lg:max-h-[calc(100vh-200px)] object-contain rounded-lg"
                            />

                            {photos.length > 1 && (
                                <button
                                    onClick={goToNext}
                                    className="absolute right-2 p-3 text-white/80 hover:text-white bg-white/10 hover:bg-white/20 rounded-full transition-colors"
                                >
                                    <ChevronRight className="w-7 h-7" />
                                </button>
                            )}
                        </>
                    )}
                </div>

                {/* Sidebar info (desktop) */}
                <div className="lg:w-72 bg-black/40 p-4 shrink-0 overflow-y-auto hidden lg:block">
                    {/* Description */}
                    {moment.description && (
                        <div className="mb-5">
                            <p className="text-gray-300 text-sm leading-relaxed">{moment.description}</p>
                        </div>
                    )}

                    {/* Caption */}
                    {caption && (
                        <div className="mb-5">
                            <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">{tm.caption || 'Caption'}</p>
                            <p className="text-gray-300 text-sm italic">"{caption}"</p>
                        </div>
                    )}

                    {/* Skills */}
                    {skills.length > 0 && (
                        <div>
                            <p className="text-xs text-gray-500 uppercase tracking-wide mb-2 flex items-center gap-1">
                                <Wrench className="w-3 h-3" />
                                {tm.form?.skills || 'Skills'}
                            </p>
                            <div className="flex flex-wrap gap-2">
                                {skills.map((skill, i) => (
                                    <span
                                        key={skill.skillid ?? i}
                                        className="text-xs bg-white/10 text-gray-300 px-2 py-1 rounded"
                                    >
                                        {getSkillName(skill)}
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Footer */}
            <div className="shrink-0 p-4 text-white">
                {/* Mobile: description + caption + skills */}
                <div className="lg:hidden mb-3 space-y-2">
                    {moment.description && (
                        <p className="text-gray-400 text-sm text-center">{moment.description}</p>
                    )}
                    {caption && (
                        <p className="text-gray-400 text-sm text-center italic">"{caption}"</p>
                    )}
                    {skills.length > 0 && (
                        <div className="flex items-center justify-center gap-2 flex-wrap">
                            <Wrench className="w-4 h-4 text-gray-500" />
                            {skills.map((skill, i) => (
                                <span key={skill.skillid ?? i} className="text-xs bg-white/10 text-gray-300 px-2 py-1 rounded">
                                    {getSkillName(skill)}
                                </span>
                            ))}
                        </div>
                    )}
                </div>

                {/* Thumbnails */}
                {photos.length > 1 && (
                    <div className="flex items-center justify-center gap-2 overflow-x-auto pb-1">
                        {photos.map((photo, index) => {
                            const thumbPath = photo.imagepath ?? photo.imagePath ?? '';
                            return (
                                <button
                                    key={index}
                                    onClick={() => setCurrentIndex(index)}
                                    className={`w-14 h-14 rounded-lg overflow-hidden border-2 shrink-0 transition-all ${
                                        index === currentIndex
                                            ? 'border-amber-500 scale-110'
                                            : 'border-transparent opacity-50 hover:opacity-100'
                                    }`}
                                >
                                    <img
                                        src={getMomentPhotoUrl(thumbPath)}
                                        alt=""
                                        className="w-full h-full object-cover"
                                    />
                                </button>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
};

export default MomentDetailPage;
